import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { IMAGES, SOFTWARE } from "@/data/content";

export default function About() {
  return (
    <div data-testid="page-about">
      <PageHero
        overline="— About Designlayer"
        title="A studio built where"
        accent="design meets production."
        body="Designlayer was created to solve two related problems: clients often struggle to understand a proposed space before it is built, and cabinetry businesses often struggle to carry approved design information accurately into production."
        image={IMAGES.textureMood}
        testid="about-hero"
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-7 space-y-6 text-[#A3A3A3] text-lg leading-relaxed">
          <p>
            Designers may create customer presentations in one program, technical drawings in
            another and manufacturing data in a third. Each transfer creates opportunities for
            missed dimensions, inconsistent finishes, incompatible hardware, omitted parts and
            production errors.
          </p>
          <p className="text-[#EAEAEA]">
            Designlayer develops a more coordinated approach. High-end visualization communicates
            the intended space with realism and precision, while Cabinet Vision and technical
            production systems convert approved design information into reliable drawings, reports,
            materials, hardware and CNC-ready manufacturing data.
          </p>
        </Reveal>
        <Reveal className="md:col-span-4 md:col-start-9" delay={120}>
          <div className="overline mb-3">— Philosophy</div>
          <p className="font-serif text-2xl leading-snug text-[#EAEAEA]">
            &ldquo;Design for the customer.<br />
            Develop for production.<br />
            <span className="text-[#B58B5D] italic">Verify for manufacturing.&rdquo;</span>
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-12 gap-10 items-start">
        <Reveal className="md:col-span-5">
          <div className="aspect-[4/5] overflow-hidden border border-white/5">
            <img src={IMAGES.founder} alt="" className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <Reveal className="md:col-span-6 md:col-start-7" delay={120}>
          <div className="overline mb-4">— The practice</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-6">A design and production specialism.</h2>
          <p className="text-[#A3A3A3] text-lg leading-relaxed">
            Interior design, kitchen and bath design, custom cabinetry production and Cabinet Vision
            specialism &mdash; connecting design intent to manufacturing requirements. The studio's
            background combines interior design, kitchen and bath planning, architectural
            visualization, custom cabinetry, production drafting, parametric cabinet development, UCS
            logic, CNC-ready documentation, shop-floor reporting and manufacturing workflow
            improvement.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-8">
            <div>
              <div className="overline mb-2">Postgraduate</div>
              <p className="text-[#EAEAEA]">Kitchen & Bath Design</p>
              <p className="text-sm text-[#A3A3A3]">Georgian College, Ontario — NKBA-accredited</p>
            </div>
            <div>
              <div className="overline mb-2">Undergraduate</div>
              <p className="text-[#EAEAEA]">Interior & Retail Design</p>
              <p className="text-sm text-[#A3A3A3]">University of Bedfordshire, UK</p>
            </div>
            <div>
              <div className="overline mb-2">Industry</div>
              <p className="text-sm text-[#A3A3A3]">Cabinet Vision UCS, CNC, S2M, production systems</p>
            </div>
            <div>
              <div className="overline mb-2">Data / Automation</div>
              <p className="text-sm text-[#A3A3A3]">Python, SQL, JSON, XML, REST APIs, VBA, Power BI</p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-[#0E0E0E] border-y border-white/5 py-24" data-testid="about-stack">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline mb-3">— Technical stack</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-12">Software & capabilities</h2>
          </Reveal>
          <div className="grid md:grid-cols-4 gap-8">
            {Object.entries(SOFTWARE).map(([k, v], idx) => (
              <Reveal key={k} delay={idx * 80}>
                <div className="overline mb-4">{k.replace(/_/g, " ")}</div>
                <ul className="space-y-2">
                  {v.map((s) => (
                    <li key={s} className="text-[#EAEAEA] text-[15px]">{s}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
